import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import SEO from "@/components/feature/SEO";
import Navbar from "@/components/base/Navbar";
import Footer from "@/components/base/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { categories, articles } from "@/mocks/articles";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const filteredArticles = articles.filter((a) => a.category === activeCategory);
  const pageTitle = currentCategory ? `${currentCategory.name} - jm下载指南` : `分类 - jm下载指南`;
  const pageDescription = currentCategory
    ? `${currentCategory.description}，jm下载权威指南`
    : `按分类浏览jm下载相关内容`;
  const keywords = `jm下载,${currentCategory?.name || ""},jm下载指南`;
  const canonicalUrl = `https://www.jmxiazai.com/category/${activeCategory}`;

  const seoMeta = {
    title: pageTitle,
    description: pageDescription,
    keywords,
    canonical: canonicalUrl,
    ogTitle: pageTitle,
    ogDescription: pageDescription,
    ogUrl: canonicalUrl,
    ogType: "website",
    ogLocale: "zh_CN",
    twitterCard: "summary",
    twitterTitle: pageTitle,
    twitterDescription: pageDescription,
  };

  return (
    <>
      <SEO meta={seoMeta} />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: currentCategory?.name || "分类" },
              ]}
            />

            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              {currentCategory?.name || "内容分类"}
            </h1>
            <p className="text-foreground-600 text-sm mb-8 max-w-2xl">
              {currentCategory?.description}
            </p>

            <div className="flex flex-wrap gap-2 mb-10">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-50 text-foreground-600 border border-background-200/70 hover:border-primary-200/60"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredArticles.map((article) => (
                  <ArticleCard
                    key={article.id}
                    slug={article.slug}
                    title={article.title}
                    description={article.description}
                    categoryName={article.categoryName}
                    date={article.date}
                    readTime={article.readTime}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-foreground-500 text-sm">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}